import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";

export function useResourceApi<T = any>(resourcePath: string) {
  const queryClient = useQueryClient();
  const baseUrl = `${API_URL}/${resourcePath}`;
  const { requestApi } = useAppContext();

  // Fetch all items
  const { data, isLoading, error } = useQuery({
    queryKey: [resourcePath],
    queryFn: () => requestApi.get(baseUrl).then((res) => res.data),
  });

  // Mutations for creating, updating, and deleting
  const create = useMutation({
    mutationFn: (newData: T) => requestApi.post(baseUrl, newData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [resourcePath] }),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: T }) =>
      requestApi.put(`${baseUrl}/${id}`, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [resourcePath] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => requestApi.delete(`${baseUrl}/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [resourcePath] }),
  });

  return {
    data,
    isLoading,
    error,
    create,
    update,
    remove,
  };
}

export function useCompoundResourceApi<T = any>(
  compoundResourcePath: string,
  resourcePath: string
) {
  const queryClient = useQueryClient();
  const baseUrl = `${API_URL}/${resourcePath}`;
  const compoundBaseUrl = `${API_URL}/${compoundResourcePath}`;
  const { requestApi } = useAppContext();
  const invalidateQuery = () =>
    queryClient.invalidateQueries({ queryKey: [compoundResourcePath] });

  // Fetch all items
  const {
    data,
    isLoading: isLoadingFetch,
    error,
  } = useQuery<T[]>({
    queryKey: [compoundResourcePath],
    queryFn: () => requestApi.get(compoundBaseUrl).then((res) => res.data),
  });

  // Mutations for creating, updating, and deleting
  const create = useMutation({
    mutationFn: (newData: T) => requestApi.post(compoundBaseUrl, newData),
    onSuccess: () => invalidateQuery(),
  });

  const createNoInvalidate = useMutation({
    mutationFn: (newData: T) => requestApi.post(compoundBaseUrl, newData),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: T }) =>
      requestApi.put(`${baseUrl}/${id}`, data),
    onSuccess: () => invalidateQuery(),
  });

  const updateNoInvalidate = useMutation({
    mutationFn: ({ id, data }: { id: string; data: T }) =>
      requestApi.put(`${baseUrl}/${id}`, data),
  });

  const remove = useMutation({
    mutationFn: (id: string) => requestApi.delete(`${baseUrl}/${id}`),
    onSuccess: () => invalidateQuery(),
  });

  return {
    data,
    isLoading:
      isLoadingFetch ||
      create.isLoading ||
      update.isLoading ||
      updateNoInvalidate.isLoading ||
      createNoInvalidate.isLoading ||
      remove.isLoading,
    error,
    create,
    update,
    updateNoInvalidate,
    remove,
    createNoInvalidate,
    invalidateQuery: invalidateQuery,
  };
}
